// /patient/vitals — log my vitals. BP / HR / weight form with recent
// readings list. Saves locally for now (no backend table yet).
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { doctorLabel, usePatientData } from "./PatientDataContext";

const RECENT = [
  { when: "Yesterday · 9:12 AM", value: "BP 136/80 · HR 70 · 181 lb", status: "OK" },
  { when: "Wed · 8:45 AM", value: "BP 142/86 · HR 74 · 182 lb", status: "HIGH" },
  { when: "Tue · 9:01 AM", value: "BP 134/78 · HR 71", status: "OK" },
];

export default function PatientVitals() {
  const { rows } = usePatientData();
  const doctor = doctorLabel(rows[0] || {});
  const [draft, setDraft] = useState({ systolic: "", diastolic: "", hr: "", weight: "" });
  const [saved, setSaved] = useState(false);
  const [readings, setReadings] = useState(RECENT);

  const handleSave = (e) => {
    e.preventDefault();
    const now = new Date();
    const stamp = now.toLocaleString(undefined, {
      weekday: "short",
      hour: "numeric",
      minute: "2-digit",
    });
    const sys = draft.systolic || "—";
    const dia = draft.diastolic || "—";
    const hr = draft.hr ? ` · HR ${draft.hr}` : "";
    const wt = draft.weight ? ` · ${draft.weight} lb` : "";
    const summary = `BP ${sys}/${dia}${hr}${wt}`;
    setReadings((cur) => [
      { when: `Today · ${stamp}`, value: summary, status: "OK" },
      ...cur,
    ]);
    setDraft({ systolic: "", diastolic: "", hr: "", weight: "" });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <>
      <div className="pp-page-head">
        <Link to="." className="pp-back-link">‹ Home</Link>
        <h1 className="pp-page-title">Log my vitals</h1>
        <div className="pp-page-sub">{doctor} asked for these</div>
      </div>

      <div className="pp-card">
        <div className="pp-visit-eyebrow" style={{ marginBottom: 4 }}>
          ♡ Today's check-in
        </div>
        <p className="pp-detail-p">
          Use your blood pressure cuff. Sit still for 1 minute, then enter the
          numbers below.
        </p>

        <form className="pp-vitals-form" onSubmit={handleSave}>
          <div className="pp-vitals-group">
            <div className="pp-vitals-group-h">♡ Blood pressure</div>
            <div className="pp-vitals-pair">
              <label className="pp-input">
                <span className="pp-input-lbl">Top number · Systolic</span>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="120"
                  value={draft.systolic}
                  onChange={(e) =>
                    setDraft((v) => ({ ...v, systolic: e.target.value }))
                  }
                />
              </label>
              <label className="pp-input">
                <span className="pp-input-lbl">Bottom number · Diastolic</span>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="80"
                  value={draft.diastolic}
                  onChange={(e) =>
                    setDraft((v) => ({ ...v, diastolic: e.target.value }))
                  }
                />
              </label>
            </div>
          </div>

          <label className="pp-input">
            <span className="pp-input-lbl">Heart rate · beats per minute</span>
            <input
              type="number"
              inputMode="numeric"
              placeholder="Tap your wrist for 30 seconds, double it."
              value={draft.hr}
              onChange={(e) => setDraft((v) => ({ ...v, hr: e.target.value }))}
            />
          </label>

          <label className="pp-input">
            <span className="pp-input-lbl">Weight (optional) · pounds</span>
            <input
              type="number"
              inputMode="decimal"
              placeholder="Optional"
              value={draft.weight}
              onChange={(e) => setDraft((v) => ({ ...v, weight: e.target.value }))}
            />
          </label>

          <div
            style={{
              gridColumn: "1 / -1",
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button type="submit" className="pp-btn pp-teal">
              ✓ Save my vitals
            </button>
            <Link to="wizard" className="pp-btn pp-ghost">
              ✨ Tell the helper instead
            </Link>
          </div>
          {saved && (
            <div className="pp-saved-flash" style={{ gridColumn: "1 / -1" }}>
              Saved — thanks!
            </div>
          )}
        </form>

        <div className="pp-section-title" style={{ margin: "20px 0 8px" }}>
          Recent readings
        </div>
        {readings.map((r, i) => (
          <div className="pp-vitals-reading" key={i}>
            <div className="pp-vitals-reading-d">📅 {r.when}</div>
            <div className="pp-vitals-reading-v">{r.value}</div>
            <span
              className={`pp-row-pill ${r.status === "HIGH" ? "pp-red" : "pp-green"}`}
            >
              {r.status}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}